/** The question changing a folder's sharing asks when it holds anything: whether what's inside takes the change too. */
import { useState } from "preact/hooks";
import type { FolderCounts } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { whatInside } from "../../lib/folders.ts";
import { Button } from "../../ui/Button.tsx";
import { DialogActions } from "../../ui/Dialog.tsx";
import { Checkbox } from "../../ui/form.tsx";

/** What's asked: what the folder holds, whether the change narrows who sees it, and (narrowing) what would stay seen
 *  more widely, once the server says. */
export interface Asking<B = unknown> {
  body: B;
  counts: FolderCounts;
  narrowing: boolean;
  wider: FolderCounts | null;
}

/**
 * "Apply to the 3 folders and 41 items inside": checked at first when the change narrows who sees the folder, not
 * when it widens it. Narrowing with it unchecked says that what's inside stays seen as it is now.
 */
export function ApplyForm(p: { asking: Asking; onCancel: () => void; onApply: (apply: boolean) => void }) {
  const { asking } = p;
  const [apply, setApply] = useState(asking.narrowing);
  const left = asking.wider && asking.wider.folders + asking.wider.items > 0 ? asking.wider : asking.counts;
  return (
    <>
      <Checkbox
        label={t("folders.share.apply", { what: whatInside(asking.counts) })}
        checked={apply}
        onCheck={setApply}
      />
      {asking.narrowing && !apply ? (
        <p role="status" class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {t("folders.share.staysWider", { what: whatInside(left) })}
        </p>
      ) : null}
      <DialogActions>
        <Button answer="cancel" onClick={p.onCancel}>
          {t("common.cancel")}
        </Button>
        <Button answer="confirm" variant="primary" onClick={() => p.onApply(apply)}>
          {t("folders.share.change")}
        </Button>
      </DialogActions>
    </>
  );
}
